import { Box, Stack, styled, Typography } from '@mui/material'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetFormSchema } from 'src/api/form-schema'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { CUSTOM_PROPERTY_LABELS } from 'src/constant'
import { CHECKLISTS_FEATURE_ENABLED } from 'src/hooks/useCompleteChecklistsDialog'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { AppForm, AppFormProps, FormSelectField, FormSubmitButton, FormTextField } from '../../components/FormCore'
import { LanguageSpecificTextField } from '../../components/LanguageSpecificTextField'
import { IPrivilegeLevel, IMainComponentType as Type } from '../../enums'
import { ICustomPropertyMainComponentType } from '../../generated-types/custom-property-main-component-type'
import { ICustomProperty } from '../../generated-types/customProperty'
import { SchemaType } from '../../generated-types/form-schema'
import { IFormType } from '../../generated-types/form-type'
import { IMainComponentType, IMainComponentTypeFormSchema } from '../../generated-types/main-component-type'
import { useZodForm } from '../../hooks/zod-form'
import { ChecklistAssignmentSection } from './ChecklistAssignmentSection'
import { EquipmentFieldPicker } from './EquipmentFieldPicker'
import { SectionHeader, SectionHeaderRule } from './EquipmentFieldPicker.style'
import { useChecklistFormActions } from './MainComponentTypeForm.action'
import { mainComponentTypeSchema } from './MainComponentTypeForm.schema'

interface MainComponentTypeFormProps extends AppFormProps {
  componentTypeCustomProperties?: ICustomProperty[]
  initialValues?: IMainComponentType
  userCustomProperties: ICustomProperty[]
  formType?: IFormType
  privilegeLevel?: IPrivilegeLevel
  customerId?: EntityId
  isLoading?: boolean
  title: string
}
export const MainComponentTypeForm: React.FC<MainComponentTypeFormProps> = ({
  onSubmit,
  onCancel,
  initialValues,
  userCustomProperties,
  componentTypeCustomProperties,
  formType = IFormType.Create,
  privilegeLevel = IPrivilegeLevel.Customer,
  customerId,
  isLoading,
  title,
}) => {
  const [customProperties, setCustomProperties] = React.useState<ICustomProperty[]>(
    componentTypeCustomProperties ?? ([] as ICustomProperty[]),
  )
  const { t: translate } = useTranslation(['main-component-type', 'common'])
  const { data: checklistSchemas } = useGetFormSchema(customerId as EntityId, { schemaType: SchemaType.Checklist })
  const componentType = Object.keys(Type)
    .filter(v => isNaN(Number(v)))
    .map(t => Type[t])

  const form = useZodForm(mainComponentTypeSchema, {
    defaultValues: initialValues,
  })
  const {
    selectedChecklists,
    availableChecklists,
    toggleChecklistMovement,
    handleAddChecklist,
    handleRemoveChecklist,
  } = useChecklistFormActions(form, checklistSchemas)

  const handleSubmit = async form => {
    const { label_en, label_no } = form
    form['name'] = JSON.stringify({ en: label_en, no: label_no })
    form['mainComponentTypeFormSchemas'] = ((form.mainComponentTypeFormSchemas ?? []) as IMainComponentTypeFormSchema[])
      .filter(mapping => mapping.formSchemaId > 0)
      .map(mapping => ({
        formSchemaId: mapping.formSchemaId,
        movementTypes: Array.from(new Set(mapping.movementTypes)),
      }))

    onSubmit(form)
  }

  useEffect(() => {
    if (componentTypeCustomProperties) {
      const customPropertyMainComponentTypes: ICustomPropertyMainComponentType[] = componentTypeCustomProperties.map(
        c => ({ customPropertyId: c.id }),
      )
      form.setValue('customPropertyMainComponentTypes', customPropertyMainComponentTypes)
      setCustomProperties(componentTypeCustomProperties as ICustomProperty[])
    }
  }, [componentTypeCustomProperties])

  useEffect(() => {
    const customPropertyMainComponentTypes: ICustomPropertyMainComponentType[] = customProperties?.map(c => ({
      customPropertyId: c.id,
    }))
    form.setValue('customPropertyMainComponentTypes', customPropertyMainComponentTypes)
  }, [customProperties])

  // When customer edits a global (super admin) equipment type, label and max count are read-only
  const isGlobalTypeEditedByCustomer =
    formType === IFormType.Update &&
    !!customerId &&
    (initialValues?.customerId == null || initialValues?.customerId === undefined)

  const handleChange = (e, value) => {
    form.setValue('componentType', value)
    if (value === Type.Net) {
      const circumferenceCustomProperty = userCustomProperties?.find(
        c => JSON.parse(c.label.toString()).en === CUSTOM_PROPERTY_LABELS.CIRCUMFERENCE,
      )
      circumferenceCustomProperty && setCustomProperties([circumferenceCustomProperty] as ICustomProperty[])
    } else if (value === Type.Ring) {
      const pipeDiameterCustomProperty = userCustomProperties?.find(
        c => JSON.parse(c.label.toString()).en === CUSTOM_PROPERTY_LABELS.PIPE_DIAMETER,
      )
      pipeDiameterCustomProperty && setCustomProperties([pipeDiameterCustomProperty] as ICustomProperty[])
    } else if (value === Type.LiceSkirt) {
      const perimeterCustomProperty = userCustomProperties?.find(
        c => JSON.parse(c.label.toString()).en === CUSTOM_PROPERTY_LABELS.PERIMETER,
      )
      perimeterCustomProperty && setCustomProperties([perimeterCustomProperty] as ICustomProperty[])
    } else {
      setCustomProperties([]) // when selected value is null
    }
  }
  return (
    <AppForm form={form} onSubmit={handleSubmit}>
      <GenericDialogLayout
        title={title}
        pop={onCancel}
        actions={[
          <FormSubmitButton
            key="submit"
            createText={
              formType == IFormType.Create ? translate('add', { ns: 'common' }) : translate('update', { ns: 'common' })
            }
            isLoading={isLoading}
          />,
        ]}
        removedCloseButton={false}
      >
        <Box minWidth={'800px'} maxHeight={'calc(100vh - 200px)'} overflow={'auto'}>
          {<FormTextField name="customerId" type="hidden" />}
          <FixedFieldsAndEquipmentFormLayout>
            <Stack direction="column" gap={1}>
              <LanguageSpecificTextField
                data={initialValues?.name || '{}'}
                label={translate('label', { ns: 'common' })}
                form={form}
                disabled={isGlobalTypeEditedByCustomer}
                stacked
              />
              <FormTextField
                type="number"
                inputProps={{ min: 1 }}
                name="maxCountForCage"
                label={translate('maxCount', { ns: 'main-component-type' })}
                disabled={isGlobalTypeEditedByCustomer}
              />
              {privilegeLevel == IPrivilegeLevel.SuperAdmin && (
                <FormSelectField
                  data={componentType!}
                  name="componentType"
                  label={translate('componentType', { ns: 'main-component-type' })}
                  getOptionLabel={option => translate(Type[option], { ns: 'main-component' })}
                  renderOption={(props: any, option) => (
                    <Box {...props} key={option}>
                      {translate(Type[option], { ns: 'main-component' })}
                    </Box>
                  )}
                  isOptionEqualToValue={(o, v) => o === v}
                  defaultValue={Type.Custom}
                  onChange={handleChange}
                />
              )}
            </Stack>

            <Stack direction="column" width={'100%'} minWidth={0}>
              <SectionHeader>
                <SubTitle>{translate('mainComponentForm', { ns: 'main-component-type' })}</SubTitle>
                <SectionHeaderRule />
              </SectionHeader>
              <Typography variant="body2" color="text.secondary" marginBottom={1.5}>
                {translate('equipmentFormDescription')}
              </Typography>
              {userCustomProperties && (
                <EquipmentFieldPicker
                  customProperties={customProperties}
                  setCustomProperties={setCustomProperties}
                  data={userCustomProperties as ICustomProperty[]}
                  lockedCustomPropertyIds={
                    formType === IFormType.Update && customerId
                      ? componentTypeCustomProperties
                          ?.filter(c => customerId == null || c.ownedCustomerId != customerId)
                          ?.map(c => c.id) ?? []
                      : []
                  }
                />
              )}
            </Stack>
          </FixedFieldsAndEquipmentFormLayout>
          {CHECKLISTS_FEATURE_ENABLED && customerId && (
            <ChecklistAssignmentSection
              availableChecklists={availableChecklists}
              selectedChecklists={selectedChecklists}
              onAddChecklist={handleAddChecklist}
              onToggleMovement={toggleChecklistMovement}
              onRemoveChecklist={handleRemoveChecklist}
              formWatch={form.watch}
            />
          )}
        </Box>
      </GenericDialogLayout>
    </AppForm>
  )
}

const SubTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  fontSize: '1.2rem',
  paddingLeft: '8px',
  color: getThemeColor(theme, MUIThemeColors.primaryMain),
}))

const FixedFieldsAndEquipmentFormLayout = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '300px 1fr',
  gap: 22,
  alignItems: 'start',
})
